import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetches a CMS content block by key. Returns { body, loading }.
 * `fallback` is used until the fetch completes AND if the block doesn't exist.
 * Cache-first: identical keys within a single tab session use the cached value.
 */
const _cache = new Map();

export function useContent(key, fallback = "") {
  const [state, setState] = useState({
    body: _cache.get(key) ?? fallback,
    loading: !_cache.has(key),
  });

  useEffect(() => {
    if (_cache.has(key)) {
      setState({ body: _cache.get(key), loading: false });
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/content/${encodeURIComponent(key)}`)
      .then((r) => {
        _cache.set(key, r.data.body);
        if (!cancelled) setState({ body: r.data.body, loading: false });
      })
      .catch(() => {
        // Missing block → use fallback silently, don't cache 404
        if (!cancelled) setState({ body: fallback, loading: false });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

/**
 * Fetches all content blocks in one round-trip. Returns { blocks, loading }.
 * Used on pages that need multiple keys at once.
 */
export function useContentAll() {
  const [state, setState] = useState({ blocks: {}, loading: true });

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => {
        Object.entries(r.data).forEach(([k, v]) => _cache.set(k, v.body));
        setState({ blocks: r.data, loading: false });
      })
      .catch(() => setState({ blocks: {}, loading: false }));
  }, []);

  return state;
}

export function invalidateContentCache(key) {
  if (key) _cache.delete(key);
  else _cache.clear();
}
